/** @file Folds per-panel catalog parts (tagged by language) into full catalogs; duplicate keys are errors. */
import { CATALOGS } from './catalogList';
import type { Catalog, PartialCatalog } from './types';

/** One generated entry of the editor's `CATALOG_PARTS`: `tag` is the language code from `i18n.<lang>.ts`. */
export interface CatalogPart {
  tag: string;
  value: object;
}

type Tree = { [key: string]: string | Tree };

/** Deep-copies `source` into `target`; a key that already exists (string or object clash) throws. */
function mergeInto(target: Tree, source: Tree, path: string, lang: string): void {
  for (const [key, value] of Object.entries(source)) {
    const here = path === '' ? key : `${path}.${key}`;
    const existing = Object.hasOwn(target, key) ? target[key] : undefined;
    if (typeof value === 'string' || typeof existing === 'string') {
      if (existing !== undefined) throw new Error(`i18n key "${here}" is defined twice for language "${lang}"`);
      target[key] = value;
      continue;
    }
    const child: Tree = existing ?? {};
    target[key] = child;
    mergeInto(child, value, here, lang);
  }
}

/**
 * Merges `base` (default: the catalogs in this package) with every part of the same language into new catalogs;
 * inputs are never mutated. A part may introduce a language. Throws when a key is defined by two sources.
 */
export function mergeCatalogParts(
  parts: readonly CatalogPart[],
  base: { en: Catalog } & Record<string, PartialCatalog> = CATALOGS,
): { en: Catalog } & Record<string, PartialCatalog> {
  const merged: Record<string, Tree> = {};
  const sources = [...Object.entries(base).map(([tag, value]) => ({ tag, value })), ...parts];
  for (const { tag, value } of sources) mergeInto((merged[tag] ??= {}), value as Tree, '', tag);
  return merged as unknown as { en: Catalog } & Record<string, PartialCatalog>;
}
